import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import BuildingSkyline from "./BuildingSkyline";
import {
  TrendingUp,
  Home,
  IndianRupee,
  CheckCircle,
  Users,
  Building2,
  Star,
  Zap,
  ArrowUpRight,
  UserPlus,
  Play,
  X,
} from "lucide-react";
import { useState, useEffect } from "react";

const notifications = [
  {
    icon: CheckCircle,
    label: "Deal Closed",
    value: "₹85L",
    color: "hsl(152 60% 48%)",
  },
  {
    icon: IndianRupee,
    label: "Commission Earned",
    value: "₹2.5L",
    color: "hsl(263 47% 55%)",
  },
  {
    icon: Home,
    label: "New Property Listed",
    value: "Satellite, 3BHK",
    color: "hsl(200 70% 50%)",
  },
  {
    icon: TrendingUp,
    label: "Property Sold",
    value: "₹1.2Cr",
    color: "hsl(30 80% 55%)",
  },
  {
    icon: Users,
    label: "New Broker Joined",
    value: "Ahmedabad",
    color: "hsl(340 65% 55%)",
  },
  {
    icon: Building2,
    label: "Builder Project Added",
    value: "SG Highway",
    color: "hsl(180 55% 45%)",
  },
  {
    icon: Star,
    label: "5★ Review Received",
    value: "Rajesh P.",
    color: "hsl(45 90% 50%)",
  },
  {
    icon: Zap,
    label: "Instant Match Found",
    value: "2 BHK Rent",
    color: "hsl(280 60% 55%)",
  },
];

const floatingCards = [
  {
    label: "Verified Owner",
    value: "Prahlad Nagar",
    position: "bottom-[35%] left-[2%]",
    mobilePosition: "bottom-[10%] left-[3%]",
    delay: 1.5,
  },
  {
    label: "Site Visit Booked",
    value: "Tomorrow 3PM",
    position: "top-[35%] left-[5%]",
    mobilePosition: "bottom-[25%] left-[5%]",
    delay: 3.2,
  },
  {
    label: "Lead Assigned",
    value: "Broker #247",
    position: "bottom-[15%] right-[8%]",
    mobilePosition: "bottom-[5%] right-[5%]",
    delay: 5.0,
  },
];

const HeroSection = () => {
  const [activeNotif, setActiveNotif] = useState(0);
  const [showNotif, setShowNotif] = useState(true);
  const [visibleCards, setVisibleCards] = useState([]);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowNotif(false);
      setTimeout(() => {
        setActiveNotif((prev) => (prev + 1) % notifications.length);
        setShowNotif(true);
      }, 400);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    floatingCards.forEach((card, i) => {
      const showCard = () => {
        setVisibleCards((prev) => [...prev.filter((x) => x !== i), i]);
        setTimeout(() => {
          setVisibleCards((prev) => prev.filter((x) => x !== i));
        }, 3000);
      };
      const initialTimeout = setTimeout(showCard, card.delay * 1000);
      const interval = setInterval(showCard, (card.delay + 6) * 1000);
      return () => {
        clearTimeout(initialTimeout);
        clearInterval(interval);
      };
    });
  }, []);

  const notif = notifications[activeNotif];
  const NotifIcon = notif.icon;

  return (
    <section className="relative min-h-[100svh] lg:flex lg:items-center overflow-hidden hero-dark">
      {/* Dot grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "radial-gradient(hsl(0 0% 100%) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />
      {/* Glow orb */}
      <div
        className="absolute top-1/4 left-1/3 w-[500px] h-[500px] rounded-full opacity-20"
        style={{
          background:
            "radial-gradient(circle, hsl(263 47% 39% / 0.3), transparent 70%)",
        }}
      />

      {/* Desktop layout */}
    <div className="hidden lg:block container mx-auto px-10 xl:px-16 2xl:px-[85px] relative z-10 pt-16 xl:pt-24">
        <div className="grid lg:grid-cols-2 gap-8 xl:gap-14 items-center">
          {/* Left — Text */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-left"
          >
            <h1 className="text-4xl xl:text-5xl 2xl:text-[3.5rem] font-bold leading-[1.12] mb-5 text-white">
              The Ultimate Property
              <br />
              Platform for Brokers
            </h1>

            <p className="text-sm xl:text-base text-white/50 max-w-md xl:max-w-lg mb-8 leading-[1.7]">
              Connect brokers with verified owner properties and builder
              projects across Ahmedabad. Close deals faster with simplified workflow.
            </p>

            <div className="flex flex-row gap-4">
              <a href="/signup">
                <Button
                  size="lg"
                  className="bg-white text-slate-900 hover:bg-white/90 shadow-lg rounded-xl h-12 px-7 text-sm font-semibold"
                >
                  <UserPlus className="mr-2 w-4 h-4" /> Register
                </Button>
              </a>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setShowVideo(true)}
                  className="border-white/20 text-white hover:bg-white/10 rounded-xl h-12 px-7 text-sm font-semibold bg-transparent"
                >
                  <Play className="mr-2 w-4 h-4" /> Live Demo
                </Button>
            </div>

            {/* Trust row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-8 flex items-center gap-4"
            >
             <div className="flex -space-x-2.5">
  {[
    "/image/white ocean.png",
    "/image/elite.png",
    "/image/spartan.png",
    "/image/res.webp",
  ].map((src, i) => (
    <img
      key={i}
      src={src}
      alt={`Partner logo ${i + 1}`}
      className="w-8 h-8 rounded-full border-2 border-[#c7abf7] object-cover bg-white"
    />
  ))}
</div>
              <div className="text-xs text-white/40">
                <span className="text-white/70 font-semibold">500+</span>{" "}
                brokers already growing
                <div className="flex gap-0.5 mt-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star
                      key={i}
                      className="w-2.5 h-2.5 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right — Buildings (desktop) */}
          <motion.div
            className="relative h-[440px]"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.7,
              delay: 0.15,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <BuildingSkyline />

            {/* Main rotating notification */}
            <AnimatePresence mode="wait">
              {showNotif && (
                <motion.div
                  key={activeNotif}
                  initial={{ opacity: 0, y: 16, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -12, scale: 0.95 }}
                  transition={{ duration: 0.35 }}
                  className="absolute top-[8%] right-[6%] glass-card-dark rounded-xl px-4 py-3 flex items-center gap-3"
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: `${notif.color}20` }}
                  >
                    <NotifIcon
                      className="w-4 h-4"
                      style={{ color: notif.color }}
                    />
                  </div>
                  <div>
                    <p className="text-white/50 text-[10px] leading-none mb-0.5 font-medium">
                      {notif.label}
                    </p>
                    <p className="font-semibold text-white text-xs">
                      {notif.value}
                    </p>
                  </div>
                  <ArrowUpRight className="w-3 h-3 text-white/20" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Floating cards (desktop) */}
            <AnimatePresence>
              {floatingCards.map(
                (card, i) =>
                  visibleCards.includes(i) && (
                    <motion.div
                      key={`float-${i}`}
                      initial={{ opacity: 0, scale: 0.9, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -8 }}
                      transition={{ duration: 0.3 }}
                      className={`absolute ${card.position} glass-card-dark rounded-lg px-3 py-2 flex items-center gap-2`}
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      <div>
                        <p className="text-white/40 text-[9px] leading-none font-medium">
                          {card.label}
                        </p>
                        <p className="font-semibold text-white text-[11px]">
                          {card.value}
                        </p>
                      </div>
                    </motion.div>
                  ),
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      {/* Mobile text content */}
      <div className="lg:hidden container mx-auto px-5 relative z-20 pt-[140px] pb-0">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <h1 className="text-[2rem] sm:text-4xl font-bold leading-[1.12] mb-4 text-white">
            The Ultimate Property
            <br />
            Platform for Brokers
          </h1>

          <p className="text-sm text-white/50 max-w-sm mx-auto mb-6 leading-[1.7]">
            Connect brokers with verified owner properties and builder projects
            across Ahmedabad. Close deals faster with simplified workflow.
          </p>

          <div className="flex flex-row gap-3 justify-center">
            <a href="/signup">
              <Button
                size="lg"
                className="bg-white text-slate-900 hover:bg-white/90 shadow-lg rounded-xl h-11 px-6 text-sm font-semibold"
              >
                <UserPlus className="mr-2 w-4 h-4" /> Register
              </Button>
            </a>
            <Button
                size="lg"
                variant="outline"
                onClick={() => setShowVideo(true)}
                className="border-white/20 text-white hover:bg-white/10 rounded-xl h-11 px-6 text-sm font-semibold bg-transparent"
              >
                <Play className="mr-2 w-4 h-4" /> Live Demo
              </Button>
          </div>

          {/* Trust row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-12 flex items-center gap-3 justify-center"
          >
            <div className="flex -space-x-2.5">
              {[
                "/image/white ocean.png",
                "/image/elite.png",
                "/image/spartan.png",
                "/image/res.webp",
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Partner logo ${i + 1}`}
                  className="w-7 h-7 rounded-full border-2 border-yellow-400 object-cover bg-white"
                />
              ))}
            </div>
            <div className="text-[11px] text-white/40">
              <span className="text-white/70 font-semibold">500+</span> brokers
              already growing
              <div className="flex gap-0.5 mt-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className="w-2.5 h-2.5 fill-amber-400 text-amber-400"
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Mobile buildings - absolute at bottom of hero, seamless */}
      <div className="lg:hidden absolute bottom-0 left-0 right-0 h-[40%] pointer-events-none z-[5]">
        <div className="absolute inset-0 flex items-end">
          <div className="w-full">
            <BuildingSkyline />
          </div>
        </div>

        {/* Floating notifications */}
        <AnimatePresence>
          {floatingCards.map(
            (card, i) =>
              visibleCards.includes(i) && (
                <motion.div
                  key={`float-mobile-${i}`}
                  initial={{ opacity: 0, scale: 0.9, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className={`absolute ${card.mobilePosition} glass-card-dark rounded-lg px-2 py-1.5 flex items-center gap-1.5 z-10 pointer-events-auto`}
                >
                  <div className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                  <div>
                    <p className="text-white/40 text-[8px] leading-none font-medium">
                      {card.label}
                    </p>
                    <p className="font-semibold text-white text-[10px]">
                      {card.value}
                    </p>
                  </div>
                </motion.div>
              ),
          )}
        </AnimatePresence>

        {/* Mobile rotating notification */}
        <div className="absolute top-6 right-4 z-20 pointer-events-auto">
          <AnimatePresence mode="wait">
            {showNotif && (
              <motion.div
                key={activeNotif}
                initial={{ opacity: 0, y: 16, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -12, scale: 0.95 }}
                transition={{ duration: 0.35 }}
                className="glass-card-dark rounded-xl px-2.5 py-2 flex items-center gap-2"
              >
                <div
                  className="w-6 h-6 rounded-lg flex items-center justify-center"
                  style={{ background: `${notif.color}20` }}
                >
                  <NotifIcon
                    className="w-3 h-3"
                    style={{ color: notif.color }}
                  />
                </div>
                <div>
                  <p className="text-white/50 text-[8px] leading-none mb-0.5 font-medium">
                    {notif.label}
                  </p>
                  <p className="font-semibold text-white text-[10px]">
                    {notif.value}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 backdrop-blur-xl bg-slate-950/40"
            onClick={() => setShowVideo(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                onClick={() => setShowVideo(false)}
              >
                <X className="w-4 h-4" />
              </button>
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/Tp8yKAQWt0M?si=RuU3lqLH8agBKZvt"
                title="Live Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default HeroSection;
